import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { v1StudentsPasswordResetQuestionCreate } from '@/api/client'
import type { ActivityEnum } from '@/api/model'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PasswordOverrideForm, type OverrideResult } from './password-override-form'
import { warningMessage } from './reset-password-copy'

const answerSchema = z.object({ answer: z.string().min(1, 'Wpisz odpowiedź.').max(256) })

export function ResetPasswordDialog({
  open,
  onOpenChange,
  login,
  activity,
  pending,
  error,
  onConfirm,
  isSuperadmin = false,
  onOverride,
  onSessionEnded,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  login: string
  activity: ActivityEnum | null
  pending?: boolean
  error?: string | null
  onConfirm: (answer: string, confirm: boolean) => Promise<void>
  /** Tylko superadmin: dodatkowa ścieżka resetu bez odpowiedzi na pytanie bezpieczeństwa. */
  isSuperadmin?: boolean
  onOverride?: (result: OverrideResult) => void
  onSessionEnded?: () => void
}) {
  const [overrideMode, setOverrideMode] = useState(false)
  const [overrideBusy, setOverrideBusy] = useState(false)
  const form = useForm<z.infer<typeof answerSchema>>({
    resolver: zodResolver(answerSchema),
    defaultValues: { answer: '' },
  })
  const questionQuery = useQuery({
    queryKey: ['student-reset-question', login],
    queryFn: () => v1StudentsPasswordResetQuestionCreate({ login }),
    enabled: open,
    retry: false,
    gcTime: 0,
  })
  const question = questionQuery.data?.status === 200 ? questionQuery.data.data.question : undefined
  const questionError = questionQuery.isError
    ? 'Nie udało się pobrać pytania.'
    : questionQuery.data && questionQuery.data.status !== 200
      ? questionQuery.data.data && 'detail' in questionQuery.data.data
        ? String(questionQuery.data.data.detail)
        : 'Reset jest niedostępny dla tego konta.'
      : null
  const close = (next: boolean) => {
    if (pending || overrideBusy) return
    if (!next) {
      form.reset()
      setOverrideMode(false)
    }
    onOpenChange(next)
  }
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset hasła ucznia</DialogTitle>
          <DialogDescription>
            Konto ucznia: <span className="font-mono">{login}</span>
          </DialogDescription>
        </DialogHeader>
        {overrideMode && onOverride ? (
          <PasswordOverrideForm
            login={login}
            onDone={onOverride}
            onSessionEnded={onSessionEnded}
            onCancel={() => setOverrideMode(false)}
            onBusyChange={setOverrideBusy}
          />
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(async ({ answer }) => {
              try {
                await onConfirm(answer, activity !== null)
              } catch {
                form.setError('answer', {
                  message: 'Nie udało się zresetować hasła. Spróbuj ponownie.',
                })
              }
            })}
          >
            {questionQuery.isPending && <p>Ładowanie pytania…</p>}
            {question && (
              <FieldGroup>
                <Field data-invalid={!!form.formState.errors.answer}>
                  <FieldLabel htmlFor="recovery-answer">{question}</FieldLabel>
                  <Input
                    id="recovery-answer"
                    type="password"
                    autoComplete="off"
                    maxLength={256}
                    disabled={pending}
                    aria-invalid={!!form.formState.errors.answer}
                    {...form.register('answer')}
                  />
                  <FieldError errors={[form.formState.errors.answer]} />
                </Field>
              </FieldGroup>
            )}
            <p className="text-sm text-muted-foreground">
              Uczeń musi podać odpowiedź ustawioną w aplikacji. Wielkość liter i skrajne spacje nie
              mają znaczenia.
            </p>
            {activity && (
              <Alert>
                <AlertDescription>{warningMessage(activity)}</AlertDescription>
              </Alert>
            )}
            {(error || questionError) && (
              <Alert variant="destructive">
                <AlertDescription>{error || questionError}</AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={() => close(false)}
              >
                Anuluj
              </Button>
              <Button type="submit" disabled={pending || !question}>
                {pending
                  ? 'Sprawdzanie…'
                  : activity
                    ? 'Kontynuuj reset'
                    : 'Sprawdź odpowiedź i resetuj'}
              </Button>
            </DialogFooter>
            {isSuperadmin && onOverride && (
              <div className="border-t pt-3">
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0"
                  disabled={pending}
                  onClick={() => setOverrideMode(true)}
                >
                  Reset bez odpowiedzi
                </Button>
                <p className="mt-1 text-xs text-muted-foreground">
                  Gdy uczeń nie ma pytania bezpieczeństwa lub nie zna odpowiedzi.
                </p>
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
